import { Anchor, Badge, Code, Group, Text, Tooltip } from '@mantine/core'
import { IconExternalLink } from '@tabler/icons-react'

import {
  type BlockchainNetwork,
  chain_configs,
} from '@repo/common/blockchain/redeem_config'
import type { BlockchainTransaction } from '@repo/common/queries/admin/redeem'

import { BlockchainAddress } from '../BlockchainAddress'

import styles from './BlockchainTransactionCard.module.css'

interface Props {
  transaction: BlockchainTransaction | null | undefined
  label?: string
}

function statusColor(status: string): string {
  switch (status) {
    case 'completed':
    case 'confirmed':
      return 'green'
    case 'pending':
    case 'broadcasting':
      return 'blue'
    case 'failed':
    case 'rejected':
      return 'red'
    default:
      return 'gray'
  }
}

function getTxExplorerUrl(
  txHash: string,
  network?: string | null,
): string | null {
  if (!network) return null
  const config = chain_configs[network as BlockchainNetwork]
  const url = config.scan_url_maker(txHash)
  return url || null
}

function truncateHash(hash: string): string {
  if (hash.length <= 16) return hash
  return `${hash.slice(0, 10)}...${hash.slice(-6)}`
}

const LinkedHash: React.FC<{
  hash: string
  network: string | null
}> = ({ hash, network }) => {
  const url = getTxExplorerUrl(hash, network)
  const truncated = truncateHash(hash)

  if (url) {
    return (
      <Tooltip label={hash} multiline maw={500}>
        <Group gap={4} wrap="nowrap" style={{ display: 'inline-flex' }}>
          <Anchor
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            size="xs"
            ff="monospace"
          >
            {truncated}
          </Anchor>
          <IconExternalLink size={12} />
        </Group>
      </Tooltip>
    )
  }

  return (
    <Tooltip label={hash} multiline maw={500}>
      <Code style={{ fontSize: '0.75rem' }}>{truncated}</Code>
    </Tooltip>
  )
}

const TxField: React.FC<{
  label: string
  children: React.ReactNode
}> = ({ label, children }) => (
  <div>
    <Text className={styles.field_label}>{label}</Text>
    <div className={styles.field_value}>{children}</div>
  </div>
)

export const BlockchainTransactionCard: React.FC<Props> = ({
  transaction,
  label,
}) => {
  if (!transaction) return null

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        {label && <Text className={styles.label}>{label}</Text>}
        <Badge
          size="xs"
          variant="light"
          color={statusColor(transaction.status)}
        >
          {transaction.status}
        </Badge>
        {transaction.network && (
          <Code style={{ fontSize: '0.7rem' }}>{transaction.network}</Code>
        )}
      </div>
      <div className={styles.fields}>
        {transaction.tx_hash && (
          <TxField label="Tx Hash">
            <LinkedHash
              hash={transaction.tx_hash}
              network={transaction.network}
            />
          </TxField>
        )}
        {transaction.source_address && (
          <TxField label="Source">
            <BlockchainAddress
              address={transaction.source_address}
              network={transaction.network}
            />
          </TxField>
        )}
        {transaction.destination_address && (
          <TxField label="Destination">
            <BlockchainAddress
              address={transaction.destination_address}
              network={transaction.network}
            />
          </TxField>
        )}
        {transaction.amount && (
          <TxField label="Amount">
            <Text size="xs" ff="monospace">
              {transaction.amount}
            </Text>
          </TxField>
        )}
        {transaction.tx_subindex != null && (
          <TxField label="Subindex">
            <Text size="xs" ff="monospace">
              {transaction.tx_subindex}
            </Text>
          </TxField>
        )}
      </div>
    </div>
  )
}
